import type { User } from '../types/user';

export function hasBridgeAccess(user: User | null): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  return user.has_bridge_access === true;
}

/**
 * True when the user can open the Bridges page. Mirrors the Bridges nav-item
 * gate (`isPlanningNavItemVisible`) and the `feature.bridges` route guard:
 * the `feature.bridges` role plus the dedicated bridge-access flag.
 */
export function canAccessBridges(user: User | null): boolean {
  return hasRole(user, 'feature.bridges') && hasBridgeAccess(user);
}

/** Check if user has a specific role key */
export function hasRole(user: User | null, roleKey: string): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  return user.role_keys?.includes(roleKey) ?? false;
}

/** Check if user has any of the specified role keys */
export function hasAnyRole(user: User | null, roleKeys: string[]): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  return roleKeys.some((key) => user.role_keys?.includes(key));
}

/**
 * True when the user is a system admin (is_admin=True) or holds the
 * Finance-approver role (feature.io_requests.finance_approve).
 * These users may edit any IO plan regardless of ownership.
 */
export function isIoPlansAdmin(user: User | null): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  return user.role_keys?.includes('feature.io_requests.finance_approve') ?? false;
}

/**
 * Role keys required for each subtab/section key.
 * Kept in sync with NAV_ITEMS roleKey values in TabNav.tsx and tab arrays
 * in page components. All recharge paths use the unified backend key.
 */
export const SUBMENU_ROLE_MAP: Record<string, string> = {
  'transactions':       'feature.invoices',
  'te':                 'feature.te',
  'accruals':           'feature.accruals',
  'postage':            'feature.postage',
  'depreciation':       'feature.depreciation',
  'mobile':             'feature.mobile_expenses',
  'io-plans':           'feature.io_plans',
  'io-requests':        'feature.io_requests',
  'mec':                'feature.mec',
  'bridges':            'feature.bridges',
  'one-pagers':         'feature.one_pagers',
  'pull-forwards':      'feature.pull_forwards',
  'preso-view':         'feature.preso',
  'travel-plans':       'feature.travel_plans',
  'recharges':          'feature.recharges',
  'reporting':          'feature.reporting',
  'report-builder':     'feature.report_builder',
  'io-priority':        'feature.io_plans',
  'ai-assistant':       'feature.ai_assistant',
  'my-tickets':         'feature.ticketing',
  'ticket-queue':       'admin.tickets.manage',
  'directory-import':   'admin.directory.manage',
  'universal-import':   'feature.imports',
};
